import { useRef, useState } from 'react'

import Badge from '@components/atoms/Badge'
import Icon from '@components/utils/Icon'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@components/atoms/HoverCard'

import { scopedTranslation } from '@utils/I18n'

import { ChangeBreakdown } from './ChangeBreakdown'

import type { ChangeBreakdownFields } from '../shifts/types'

const t = scopedTranslation('roster_board')

export function ChangeBreakdownDisclosure({
  breakdown,
  roleName,
}: {
  breakdown: ChangeBreakdownFields
  roleName?: string
}) {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dismissed = useRef(false)

  const close = () => {
    dismissed.current = true
    setPinned(false)
    setOpen(false)
  }

  const togglePin = () => {
    if (pinned) {
      close()
    } else {
      setPinned(true)
      setOpen(true)
    }
  }

  return (
    <HoverCard
      open={open}
      openDelay={100}
      closeDelay={100}
      onOpenChange={(next) => {
        if (pinned && !next) return
        if (next && dismissed.current) return
        setOpen(next)
      }}
    >
      <HoverCardTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={t('changes.trigger_aria', { role: roleName ?? '' })}
          aria-expanded={open}
          aria-pressed={pinned}
          onClick={togglePin}
          onPointerEnter={() => {
            dismissed.current = false
          }}
          onFocus={() => {
            dismissed.current = false
          }}
          className="tw:cursor-pointer tw:rounded-full tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-warning-400"
        >
          <Badge color="warning" leadingIcon="alert-circle">
            {t('status_unpublished_changes')}
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={6}
        className="tw:relative tw:z-30 tw:w-72"
        onInteractOutside={(event) => {
          if (triggerRef.current?.contains(event.target as Node)) {
            event.preventDefault()
            return
          }
          close()
        }}
        onEscapeKeyDown={() => {
          if (pinned) close()
        }}
      >
        {pinned && (
          <button
            type="button"
            onClick={close}
            className="tw:absolute tw:right-2 tw:top-2 tw:flex tw:h-6 tw:w-6 tw:items-center tw:justify-center tw:rounded-full tw:text-gray-400 tw:hover:bg-gray-100 tw:hover:text-gray-700 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-warning-400"
          >
            <Icon type="x-close" className="tw:h-3.5 tw:w-3.5" />
            <span className="tw:sr-only">{t('changes.close')}</span>
          </button>
        )}
        <ChangeBreakdown breakdown={breakdown} />
      </HoverCardContent>
    </HoverCard>
  )
}
